import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { useRetracked } from 'js/lib/retracked';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import {
  createInitialDegreeFilterState,
  degreeFilterReducer,
} from 'bundles/premium-hub/components/degrees/useDegreeFilterReducer';
import {
  DEGREE_HUB_APPLICATION_OPEN_OPTIONS,
  DEGREE_HUB_PRODUCT_VARIANT_FILTERS,
  HubPages,
  ProductVariant,
} from 'bundles/premium-hub/constants';
import type { PremiumHubPageType } from 'bundles/premium-hub/constants';
import type { Domain, PremiumProductWithMetadata } from 'bundles/premium-hub/types';
import type { FilterBucket } from 'bundles/premium-hub/utils/degreeFilterBuckets';
import {
  getDurationBucketId,
  getDurationBuckets,
  getDurationNotSpecifiedBucket,
  getOpenContentAvailabilityBuckets,
  getTuitionBucketId,
  getTuitionBuckets,
  getTuitionNotAvailableBucket,
} from 'bundles/premium-hub/utils/degreeFilterBuckets';

type StoredState = {
  educationLevel?: number;
  productVariant?: string;
  domain?: string;
  applicationOpen?: string;
  costBucket?: string;
  durationBucket?: string;
  openContentAvailabilityBucket?: string;
  epochTimestamp: number;
};

type InputProps = {
  degreeListWithMetadata?: Array<PremiumProductWithMetadata>;
  pathname?: string;
  domains?: Domain[];
  currentPageType?: PremiumHubPageType;
  reconcileFilters?: boolean;
};

type StateForChildren = {
  filteredDegreeList?: Array<PremiumProductWithMetadata>;
};

type StateValues = {
  selectedProductVariant?: string;
  selectedDomain?: string;
  selectedApplicationOpen?: string;
  submittedProductVariant?: string;
  submittedDomain?: string;
  submittedApplicationOpen?: string;
  submittedCostBucket?: string;
  submittedDurationBucket?: string;
  submittedOpenContentAvailabilityBucket?: string;
  availableProductVariantFilters?: string[];
  availableDomainFilters?: Domain[];
  availableApplicationOpenFilters?: { id: string; name: string }[];
  availableCostFilters?: FilterBucket[];
  availableDurationFilters?: FilterBucket[];
  availableOpenContentAvailabilityFilters?: FilterBucket[];
  selectedProductVariantValue?: string[];
  selectedDomainValue?: string[];
  selectedApplicationOpenValue?: string;
  selectedCostValue?: string[];
  selectedDurationValue?: string[];
  selectedOpenContentAvailabilityValue?: string[];
  open?: boolean;
};

type StateSetters = {
  setSelectedProductVariant: (productVariant: string | undefined) => void;
  setSelectedDomain: (domain: string | undefined) => void;
  setSelectedApplicationOpen: (applicationOpen: string | undefined) => void;
  setSubmittedProductVariant: (productVariant: string | undefined) => void;
  setSubmittedDomain: (domain: string | undefined) => void;
  setSubmittedApplicationOpen: (applicationOpen: string | undefined) => void;
  setSelectedCost: (cost: string | undefined) => void;
  setSubmittedCostBucket: (cost: string | undefined) => void;
  setSelectedDuration: (duration: string | undefined) => void;
  setSubmittedDurationBucket: (duration: string | undefined) => void;
  setSelectedOpenContentAvailability: (availability: string | undefined) => void;
  setSubmittedOpenContentAvailabilityBucket: (availability: string | undefined) => void;
  clearAll?: () => void;
  setOpen?: (open: boolean) => void;
};

export type DegreeFilterState = StateValues & StateSetters;

export type PropsFromDegreeFilterStateManager = InputProps &
  StateForChildren & { degreeFilterState: DegreeFilterState };

const filterFunctionFactory =
  (
    productVariant?: string,
    domain?: string,
    applicationOpen?: string,
    costBucket?: string,
    durationBucket?: string,
    openContentAvailabilityBucket?: string
  ) =>
  (degree: PremiumProductWithMetadata): boolean => {
    if (
      !productVariant &&
      !domain &&
      !applicationOpen &&
      !costBucket &&
      !durationBucket &&
      !openContentAvailabilityBucket
    ) {
      return true;
    }

    const productVariantArray = productVariant ? productVariant.split(',') : [];
    const domainArray = domain ? domain.split(',') : [];
    const costBucketArray = costBucket ? costBucket.split(',') : [];
    const durationBucketArray = durationBucket ? durationBucket.split(',') : [];
    const openContentAvailabilityArray = openContentAvailabilityBucket ? openContentAvailabilityBucket.split(',') : [];

    const isNextAdmissionSubtermDeadlineDateValid = Boolean(
      degree?.upcomingTermDates?.nextAdmissionSubtermDeadlineDate?.seconds
    );

    const productVariantFilter = productVariantArray.length
      ? productVariantArray.includes(degree.productVariant ?? '')
      : true;
    const domainFilter = domainArray.length
      ? !!domainArray.filter((domainItem) => degree.domainIds.includes(domainItem)).length
      : true;
    const applicationOpenFilter =
      applicationOpen === 'onPlatform'
        ? isNextAdmissionSubtermDeadlineDateValid
        : applicationOpen === 'offPlatform'
          ? !isNextAdmissionSubtermDeadlineDateValid
          : true;
    const costFilter = costBucketArray.length
      ? costBucketArray.includes(getTuitionBucketId(degree.degreeAttributes?.tuitionInUsd))
      : true;
    const durationFilter = durationBucketArray.length
      ? durationBucketArray.includes(getDurationBucketId(degree.atAGlance?.timeFrame))
      : true;
    const openContentAvailabilityFilter = openContentAvailabilityArray.length
      ? openContentAvailabilityArray.includes(degree.openContentAvailability ?? '')
      : true;

    return (
      productVariantFilter &&
      domainFilter &&
      applicationOpenFilter &&
      costFilter &&
      durationFilter &&
      openContentAvailabilityFilter
    );
  };

const LOCALSTORAGE_PREFIX_SUBMITTED = 'storedDegreeFilterState_submitted_multiselect_';
const LOAD_PREV_STATE_TIMEOUT_MINS = 3;

export const convertPathnameToSlug = (pathname: string) =>
  pathname
    .split('/')
    .filter((part) => part.length > 0)
    .join('-');

export const getLocalStorageKey = (pathname: string) =>
  `${LOCALSTORAGE_PREFIX_SUBMITTED}${convertPathnameToSlug(pathname)}`;

export const setDegreeFilterLocalStorage = (
  pathname: string,
  productVariant: string | undefined,
  domain: string | undefined,
  applicationOpen: string | undefined,
  costBucket?: string,
  durationBucket?: string,
  openContentAvailabilityBucket?: string
) => {
  const temp: StoredState = {
    productVariant,
    domain,
    applicationOpen,
    costBucket,
    durationBucket,
    openContentAvailabilityBucket,
    epochTimestamp: new Date().getTime(),
  };
  localStorageEx.setItem<StoredState>(getLocalStorageKey(pathname), temp, JSON.stringify);
};

/**
 *
 * @param pathname The pathname of the current route. Ex: "/degrees/computer-science/"
 * @returns StoredState = {
 *   educationLevel?: number | undefined;
 *   productVariant?: string | undefined;
 *   domain?: string | undefined;
 *   epochTimestamp: number;
 * }
 */
export const getDegreeFilterLocalStorage = (pathname: string): StoredState | null => {
  const prevState: StoredState | null = localStorageEx.getItem(getLocalStorageKey(pathname), JSON.parse, null);
  if (!prevState) return null;
  const { epochTimestamp } = prevState;
  const now = new Date().getTime();
  const differenceMins = (now - epochTimestamp) / 60000;
  if (differenceMins >= LOAD_PREV_STATE_TIMEOUT_MINS) return null;
  return prevState;
};

export const useMultiSelectDegreeFilterStateManager = ({
  degreeListWithMetadata,
  pathname,
  domains = [],
  currentPageType,
  reconcileFilters = true,
}: InputProps): PropsFromDegreeFilterStateManager => {
  const [state, dispatch] = useReducer(degreeFilterReducer, undefined, createInitialDegreeFilterState);
  const {
    selectedProductVariant,
    selectedDomain,
    selectedApplicationOpen,
    selectedCost,
    selectedDuration,
    selectedOpenContentAvailability,
    submittedProductVariant,
    submittedDomain,
    submittedApplicationOpen,
    submittedCostBucket,
    submittedDurationBucket,
    submittedOpenContentAvailabilityBucket,
    open,
    startupComplete,
  } = state;

  const trackComponent = useRetracked();

  // Only show the postgraduate filter on DCAT/Geo pages
  const productVariantFilters = [
    ...DEGREE_HUB_PRODUCT_VARIANT_FILTERS,
    ...(currentPageType === HubPages.DegreesCategoryHubPage ? [ProductVariant.PostgraduateDiploma] : []),
  ];

  const filteredDegreeList = useMemo(
    () =>
      degreeListWithMetadata?.filter(
        filterFunctionFactory(
          submittedProductVariant,
          submittedDomain,
          submittedApplicationOpen,
          submittedCostBucket,
          submittedDurationBucket,
          submittedOpenContentAvailabilityBucket
        )
      ) ?? [],
    [
      degreeListWithMetadata,
      submittedProductVariant,
      submittedDomain,
      submittedApplicationOpen,
      submittedCostBucket,
      submittedDurationBucket,
      submittedOpenContentAvailabilityBucket,
    ]
  );

  useEffect(() => {
    if (!degreeListWithMetadata?.length && startupComplete) return;
    if (!pathname) return;
    dispatch({ type: 'HYDRATE_FROM_STORAGE', payload: getDegreeFilterLocalStorage(pathname) });
    /**
     * We only want to run this useEffect block on startup, which is the only time that degreeListWithMetadata would change
     * (from undefined to a populated array after queries finish)
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [degreeListWithMetadata]);

  useEffect(() => {
    if (!startupComplete || !filteredDegreeList || filteredDegreeList.length < 1) return;
    setDegreeFilterLocalStorage(
      pathname ?? '',
      submittedProductVariant,
      submittedDomain,
      submittedApplicationOpen,
      submittedCostBucket,
      submittedDurationBucket,
      submittedOpenContentAvailabilityBucket
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pathname,
    submittedDomain,
    submittedProductVariant,
    submittedApplicationOpen,
    submittedCostBucket,
    submittedDurationBucket,
    submittedOpenContentAvailabilityBucket,
  ]);

  const selectedProductVariantValue = useMemo(
    () => (selectedProductVariant ? selectedProductVariant.split(',') : []),
    [selectedProductVariant]
  );
  const selectedDomainValue = useMemo(() => (selectedDomain ? selectedDomain.split(',') : []), [selectedDomain]);
  const selectedApplicationOpenValue = selectedApplicationOpen;
  const selectedCostValue = useMemo(() => (selectedCost ? selectedCost.split(',') : []), [selectedCost]);
  const selectedDurationValue = useMemo(
    () => (selectedDuration ? selectedDuration.split(',') : []),
    [selectedDuration]
  );
  const selectedOpenContentAvailabilityValue = useMemo(
    () => (selectedOpenContentAvailability ? selectedOpenContentAvailability.split(',') : []),
    [selectedOpenContentAvailability]
  );

  const setSelectedProductVariant = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_PRODUCT_VARIANT', payload: value }),
    []
  );
  const setSelectedDomain = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_DOMAIN', payload: value }),
    []
  );
  const setSelectedApplicationOpen = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_APPLICATION_OPEN', payload: value }),
    []
  );
  const setSelectedCost = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_COST', payload: value }),
    []
  );
  const setSelectedDuration = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_DURATION', payload: value }),
    []
  );
  const setSelectedOpenContentAvailability = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SELECTED_OPEN_CONTENT_AVAILABILITY', payload: value }),
    []
  );
  const setSubmittedProductVariant = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_PRODUCT_VARIANT', payload: value }),
    []
  );
  const setSubmittedDomain = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_DOMAIN', payload: value }),
    []
  );
  const setSubmittedApplicationOpen = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_APPLICATION_OPEN', payload: value }),
    []
  );
  const setSubmittedCostBucket = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_COST_BUCKET', payload: value }),
    []
  );
  const setSubmittedDurationBucket = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_DURATION_BUCKET', payload: value }),
    []
  );
  const setSubmittedOpenContentAvailabilityBucket = useCallback<(value: string | undefined) => void>(
    (value) => dispatch({ type: 'SET_SUBMITTED_OPEN_CONTENT_AVAILABILITY_BUCKET', payload: value }),
    []
  );
  const setOpen = useCallback<(value: boolean) => void>((value) => dispatch({ type: 'SET_OPEN', payload: value }), []);

  const clearAll = () => {
    trackComponent({
      trackingData: {},
      trackingName: 'multiselect_filters_reset',
      action: 'click',
    });
    dispatch({ type: 'CLEAR_ALL' });
  };

  const availableProductVariantFilters = useMemo(
    () => {
      let optionsResult = [];
      if (selectedDomainValue.length > 0) {
        const productVariantOptions =
          degreeListWithMetadata?.reduce((options, degree) => {
            if (
              degree.productVariant &&
              selectedDomainValue.filter((domain) => degree.domainIds?.includes(domain)).length
            ) {
              options.add(degree.productVariant);
            }
            return options;
          }, new Set<string>()) ?? new Set();
        optionsResult = Array.from(productVariantOptions);
      } else {
        optionsResult = productVariantFilters;
      }
      return optionsResult.sort();
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [degreeListWithMetadata, selectedDomainValue]
  );

  const availableDomainFilters = useMemo(() => {
    const domainOptions =
      degreeListWithMetadata?.reduce((options, degree) => {
        if (degree.domainIds?.length) {
          if (
            selectedProductVariantValue.length === 0 ||
            selectedProductVariantValue.includes(degree.productVariant ?? '')
          ) {
            for (const id of degree.domainIds) {
              options.add(id);
            }
          }
        }
        return options;
      }, new Set<string>()) ?? new Set();
    return domains.filter((domain) => domainOptions.has(domain.id));
  }, [degreeListWithMetadata, domains, selectedProductVariantValue]);

  const availableApplicationOpenFilters = DEGREE_HUB_APPLICATION_OPEN_OPTIONS;

  const availableCostFilters = useMemo(() => {
    const presentBucketIds = new Set(
      degreeListWithMetadata?.map((degree) => getTuitionBucketId(degree.degreeAttributes?.tuitionInUsd))
    );
    const buckets: FilterBucket[] = getTuitionBuckets().filter(({ id }) => presentBucketIds.has(id));
    if (presentBucketIds.has(getTuitionNotAvailableBucket().id)) {
      buckets.push(getTuitionNotAvailableBucket());
    }
    return buckets;
  }, [degreeListWithMetadata]);

  const availableDurationFilters = useMemo(() => {
    const presentBucketIds = new Set(
      degreeListWithMetadata?.map((degree) => getDurationBucketId(degree.atAGlance?.timeFrame))
    );
    const buckets: FilterBucket[] = getDurationBuckets().filter(({ id }) => presentBucketIds.has(id));
    if (presentBucketIds.has(getDurationNotSpecifiedBucket().id)) {
      buckets.push(getDurationNotSpecifiedBucket());
    }
    return buckets;
  }, [degreeListWithMetadata]);

  const availableOpenContentAvailabilityFilters = useMemo(() => getOpenContentAvailabilityBuckets(), []);

  useEffect(() => {
    if (!reconcileFilters) return;
    const filteredProductVariants = selectedProductVariantValue.filter((productVariant) =>
      availableProductVariantFilters.includes(productVariant)
    );
    const filteredProductVariantStr = filteredProductVariants.length === 0 ? '' : filteredProductVariants.join(',');

    const filteredDomains = selectedDomainValue.filter(
      (domainId) => availableDomainFilters.filter((domain) => domain.id === domainId).length
    );
    const filteredDomainStr = filteredDomains.length === 0 ? '' : filteredDomains.join(',');

    // web/mobile will take differenct actions
    if (open) {
      setSelectedProductVariant(filteredProductVariantStr);
      setSelectedDomain(filteredDomainStr);
    } else {
      setSubmittedProductVariant(filteredProductVariantStr);
      setSubmittedDomain(filteredDomainStr);
    }
  }, [
    availableProductVariantFilters,
    availableDomainFilters,
    selectedProductVariantValue,
    selectedDomainValue,
    open,
    reconcileFilters,
    setSelectedProductVariant,
    setSelectedDomain,
    setSubmittedProductVariant,
    setSubmittedDomain,
  ]);

  return {
    filteredDegreeList,
    degreeFilterState: {
      selectedProductVariant,
      setSelectedProductVariant,
      selectedDomain,
      setSelectedDomain,
      selectedApplicationOpen,
      setSelectedApplicationOpen,
      selectedOpenContentAvailabilityValue,
      setSelectedOpenContentAvailability,
      submittedProductVariant,
      setSubmittedProductVariant,
      submittedDomain,
      setSubmittedDomain,
      submittedApplicationOpen,
      setSubmittedApplicationOpen,
      submittedCostBucket,
      setSubmittedCostBucket,
      submittedDurationBucket,
      setSubmittedDurationBucket,
      setSelectedCost,
      setSelectedDuration,
      submittedOpenContentAvailabilityBucket,
      setSubmittedOpenContentAvailabilityBucket,
      availableProductVariantFilters,
      availableDomainFilters,
      availableApplicationOpenFilters,
      availableCostFilters,
      availableOpenContentAvailabilityFilters,
      availableDurationFilters,
      selectedProductVariantValue,
      selectedDomainValue,
      selectedApplicationOpenValue,
      selectedCostValue,
      selectedDurationValue,
      clearAll,
      open,
      setOpen,
    },
  };
};

export default useMultiSelectDegreeFilterStateManager;
